import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, ShieldCheck, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["Charpente", "Couverture", "Bardage", "Rénovation complète", "Autre projet"];

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-white placeholder:text-zinc-600 focus:outline-none focus:border-jaune transition-colors duration-300 text-base";

export default function Contact() {
  const [form, setForm] = useState({ nom: "", telephone: "", email: "", commune: "", type_travaux: "Couverture", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/devis`, form);
      toast.success("Demande envoyée ! Nous vous recontactons sous 24 à 48h.", { duration: 6000 });
      setForm({ nom: "", telephone: "", email: "", commune: "", type_travaux: "Couverture", message: "" });
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez ou écrivez-nous à contact@goujonvallee.fr");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-28 lg:py-40 border-t border-white/10 bg-[#0C0C0E]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xs tracking-[0.3em] uppercase text-jaune font-bold mb-4">Contact & Devis</p>
          <h2 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl md:text-5xl leading-tight">
            Parlons de votre toit
          </h2>
          <p className="mt-6 text-base text-zinc-400 leading-relaxed font-light max-w-md">
            Décrivez-nous votre projet : nous vous rappelons sous 24 à 48 heures ouvrées
            pour convenir d'un rendez-vous et établir un devis gratuit et détaillé.
          </p>
          <div className="mt-12 space-y-6">
            <a href="mailto:contact@goujonvallee.fr" data-testid="contact-email-link" className="flex items-center gap-4 text-zinc-300 hover:text-jaune transition-colors duration-300">
              <Mail className="w-5 h-5 text-jaune shrink-0" />
              <span className="text-base">contact@goujonvallee.fr</span>
            </a>
            <a href="tel:+33235603633" data-testid="contact-phone-link" className="flex items-center gap-4 text-zinc-300 hover:text-jaune transition-colors duration-300">
              <Phone className="w-5 h-5 text-jaune shrink-0" />
              <span className="text-base">02 35 60 36 33</span>
            </a>
            <div className="flex items-center gap-4 text-zinc-300">
              <MapPin className="w-5 h-5 text-jaune shrink-0" />
              <span className="text-base">330 rue de l'Avenir, 27310 Bosgouet</span>
            </div>
            <div className="flex items-center gap-4 text-zinc-400">
              <Clock className="w-5 h-5 text-jaune shrink-0" />
              <span className="text-sm">Réponse sous 24 à 48h ouvrées</span>
            </div>
            <div className="flex items-center gap-4 text-zinc-400">
              <ShieldCheck className="w-5 h-5 text-jaune shrink-0" />
              <span className="text-sm">Devis gratuit, sans engagement — partout en Normandie</span>
            </div>
          </div>
          <div className="mt-12 border border-white/10 overflow-hidden">
            <img
              src="/realisations/p1.jpg"
              alt="Camion Goujon Vallée sur un chantier en Normandie"
              loading="lazy"
              className="w-full h-64 object-cover"
              data-testid="contact-van-image"
            />
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="devis-form"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="space-y-8"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <label htmlFor="devis-nom" className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Nom *</label>
              <input id="devis-nom" data-testid="devis-input-nom" required value={form.nom} onChange={set("nom")} placeholder="Votre nom" className={inputCls} />
            </div>
            <div>
              <label htmlFor="devis-tel" className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Téléphone *</label>
              <input id="devis-tel" data-testid="devis-input-telephone" required type="tel" value={form.telephone} onChange={set("telephone")} placeholder="06 00 00 00 00" className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <label htmlFor="devis-email" className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Email *</label>
              <input id="devis-email" data-testid="devis-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="vous@exemple.fr" className={inputCls} />
            </div>
            <div>
              <label htmlFor="devis-commune" className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Commune *</label>
              <input id="devis-commune" data-testid="devis-input-commune" required value={form.commune} onChange={set("commune")} placeholder="Caen, Lisieux, Rouen…" className={inputCls} />
            </div>
          </div>
          <div>
            <label htmlFor="devis-type" className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Type de travaux *</label>
            <select id="devis-type" data-testid="devis-select-type" value={form.type_travaux} onChange={set("type_travaux")} className={`${inputCls} cursor-pointer`}>
              {TYPES.map((t) => (
                <option key={t} value={t} className="bg-[#121214] text-white">{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="devis-message" className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold">Votre projet</label>
            <textarea id="devis-message" data-testid="devis-input-message" rows={4} value={form.message} onChange={set("message")} placeholder="Décrivez votre projet : surface, nature des travaux, délais souhaités…" className={`${inputCls} resize-none`} />
          </div>
          <button
            type="submit"
            data-testid="devis-submit-button"
            disabled={loading}
            className="group w-full flex items-center justify-center gap-3 bg-white text-black px-8 py-5 text-xs tracking-[0.2em] uppercase font-bold hover:bg-jaune transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                Envoyer ma demande de devis
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </>
            )}
          </button>
          <p className="text-xs text-zinc-600 leading-relaxed">
            Vos données servent uniquement à traiter votre demande de devis et ne sont jamais transmises à des tiers.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
